import React from "react";
import Input from "@cs/components/src/Input";
import ComponentTypeBadge from "../configurePluginOrScript/ComponentTypeBadge";
import Table from "@cs/components/src/Table";
import styled from "styled-components";
import Checkbox from "@cs/components/src/Checkbox";
import { useMutation, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import Button from "@cs/components/src/Button";
import { Play } from "react-feather";
import Banner from "@cs/components/src/Banner";
import CronInput from "../configurePluginOrScript/CronInput";
import Select, { Option } from "@cs/components/src/Select";

interface Props {
  pluginInstance: PluginInstanceItem;
  pluginConfig: PluginConfigItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}

const columns = [
  { title: "Component", width: "1fr" },
  { title: "Options", width: "1.5fr" },
  { title: "Enabled", width: "60px" },
];

const StyledRow = styled.div`
  display: grid;
  grid-template-columns: ${columns.map(({ width }) => width).join(" ")};
  column-gap: 10px;
  align-items: center;
`;

const Title = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 5px;
  }
`;

const CronWrapper = styled.div`
  grid-column: span 3;
  margin-top: 10px;
  padding: 0 15px;

  > div > div {
    margin-bottom: 10px;
  }
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  /* grid-template-columns: 1fr 1fr;
  column-gap: 10px; */

  > div {
    margin-bottom: 5px;
  }
`;

const PluginInstanceComponent = (props: {
  component: PluginConfigItem["components"][0];
  pluginInstance: PluginInstanceItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}) => {
  const { component, pluginInstance, onChange } = props;
  const titleInstance = pluginInstance.titles?.find(
    ({ customActionConfigId }) => customActionConfigId === component.id
  );
  const aclGroupsInstance = pluginInstance.acl_groups?.find(
    ({ customActionConfigId }) => customActionConfigId === component.id
  );
  const cronInstance = pluginInstance.crons?.find(
    ({ scheduleConfigId }) => scheduleConfigId === component.id
  );
  const queryClient = useQueryClient();

  const runMutation = useMutation({
    mutationFn: async ({
      componentId,
      local,
    }: {
      componentId: string;
      local?: boolean;
    }) => {
      await ApiService.fetch(
        "POST",
        `/api/run/${pluginInstance.id}/${componentId}${
          local ? "?local=true" : ""
        }`,
        {
          system_domain_id: pluginInstance.systemDomainId,
        }
      );
      queryClient.invalidateQueries(["jobList"]);
    },
  });

  return (
    <StyledRow>
      <Title>
        <ComponentTypeBadge type={component.type} />
        <span>{component.title}</span>
      </Title>
      {component.type === "custom_action_config" ? (
        <InputColumn>
          <Input
            label="Custom Title"
            key={component.id}
            value={titleInstance?.title ?? ""}
            // rightLabel={`(Default: ${component.title})`}
            placeholder={component.title}
            onChange={(value) => {
              const obj = {
                customActionConfigId: component.id,
                title: value,
              };
              if (titleInstance) {
                onChange({
                  ...pluginInstance,
                  titles: (pluginInstance.titles ?? []).map((titleInstance) =>
                    titleInstance.customActionConfigId === component.id
                      ? obj
                      : titleInstance
                  ),
                });
              } else {
                onChange({
                  ...pluginInstance,
                  titles: [...(pluginInstance.titles ?? []), obj],
                });
              }
            }}
          />
          <Select
            optionsAsync={{
              key: `${pluginInstance.systemDomainId}/team`,
              fn: async () =>
                (
                  await ApiService.fetch<{ data: Option[] }>(
                    "GET",
                    `/api/select_option/${pluginInstance.systemDomainId}/team`
                  )
                ).data,
            }}
            value={aclGroupsInstance?.groups}
            isMulti
            label="ACL Teams"
            onClear={() => {
              onChange({
                ...pluginInstance,
                acl_groups: pluginInstance.acl_groups?.filter(
                  ({ customActionConfigId }) =>
                    customActionConfigId !== component.id
                ),
              });
            }}
            onChange={(value) => {
              const obj = {
                customActionConfigId: component.id,
                groups: value.map(({ value }) => value ?? ""),
              };
              if (aclGroupsInstance) {
                onChange({
                  ...pluginInstance,
                  acl_groups: (pluginInstance.acl_groups ?? []).map(
                    (aclGroupsInstance) =>
                      aclGroupsInstance.customActionConfigId === component.id
                        ? obj
                        : aclGroupsInstance
                  ),
                });
              } else {
                onChange({
                  ...pluginInstance,
                  acl_groups: [...(pluginInstance.acl_groups ?? []), obj],
                });
              }
            }}
            portalTarget={document.getElementById("PortalTarget")}
            placeholder="Everyone"
          />
        </InputColumn>
      ) : component.type === "schedule_config" ? (
        <div style={{ display: "flex", gap: 5 }}>
          <Button
            label="Run Now"
            type="success"
            icon={Play}
            onClick={() => runMutation.mutate({ componentId: component.id })}
            loading={runMutation.isLoading}
          />
          <Button
            label="Run Local"
            type="default"
            borderless
            onClick={() =>
              runMutation.mutate({ componentId: component.id, local: true })
            }
            loading={runMutation.isLoading}
          />
          {runMutation.isError && <Banner error="Error running" />}
        </div>
      ) : (
        <div />
      )}
      <Checkbox
        value={!pluginInstance.disabledComponents?.includes(component.id)}
        onChange={(enabled) => {
          if (enabled) {
            onChange({
              ...pluginInstance,
              disabledComponents: pluginInstance.disabledComponents?.filter(
                (id) => id !== component.id
              ),
            });
          } else {
            onChange({
              ...pluginInstance,
              disabledComponents: [
                ...(pluginInstance.disabledComponents ?? []),
                component.id,
              ],
            });
          }
        }}
      />
      {component.type === "schedule_config" && (
        <CronWrapper>
          <CronInput
            value={cronInstance?.cron ?? component.cron}
            onChange={(value) => {
              const obj = {
                scheduleConfigId: component.id,
                cron: value,
              };
              if (cronInstance) {
                onChange({
                  ...pluginInstance,
                  crons: (pluginInstance.crons ?? []).map((cronInstance) =>
                    cronInstance.scheduleConfigId === component.id
                      ? obj
                      : cronInstance
                  ),
                });
              } else {
                onChange({
                  ...pluginInstance,
                  crons: [...(pluginInstance.crons ?? []), obj],
                });
              }
            }}
          />
        </CronWrapper>
      )}
    </StyledRow>
  );
};

const PluginLabelComponent = (props: {
  pluginInstance: PluginInstanceItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}) => {
  const { pluginInstance, onChange } = props;
  return (
    <StyledRow>
      <Title>
        <ComponentTypeBadge type="iconik_plugin_config" />
      </Title>
      <InputColumn>
        <Input
          label="Label"
          value={pluginInstance.label ?? ""}
          onChange={(value) => onChange({ ...pluginInstance, label: value })}
          noPadding
        />
      </InputColumn>
    </StyledRow>
  );
};

const Wrapper = styled.div`
  /* padding-top: 20px; */
`;

const PluginInstanceComponentList = (props: Props) => {
  const { pluginInstance, pluginConfig, onChange } = props;

  return (
    <Wrapper>
      <Table
        columns={columns}
        rows={[
          {
            id: "plugin",
            element: () => (
              <PluginLabelComponent
                pluginInstance={pluginInstance}
                onChange={onChange}
              />
            ),
          },
          ...pluginConfig.components
            .filter(
              (component) =>
                component.type !== "list_input_config" &&
                !(
                  component.type === "custom_action_config" &&
                  component.internal
                )
            )
            .map((component) => ({
              id: component.id,
              element: () => (
                <PluginInstanceComponent
                  component={component}
                  pluginInstance={pluginInstance}
                  onChange={onChange}
                />
              ),
            })),
        ]}
        autoHeight={{ twoColumn: true }}
      />
    </Wrapper>
  );
};

export default PluginInstanceComponentList;
